import React from 'react';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import * as PropTypes from 'prop-types';

import { getPieChartSegmentColors, PIE_CHART_COLOR_SCHEME } from './colorSchemes';
import PieChartLegend from './pieChartLegend';

import classes from './index.module.css';

const PieChartGraph = dynamic(() => import('./pieChartGraph'), { ssr: false });

const PieChart = (props) => {
  const {
    pieChartDiameter, // dieameter in pixels
    pieChartData, // array [] formatted data
    colorScheme, // custom PIE_CHART_COLOR_SCHEME
    segmentsTooltipStyles, // custom styles for hover tooltip
    chartIsClickable, // each segment is a link
    samePageLinks, // adds smooth scroll
    legendHeader, // content above legend section
    legendFooter, // content below legend section
    graphHeader, // content above pie graph section
    graphFooter, // content below pie graph section
    isGraphFooterAfterLegend,
    showLegends,
    compact, // cutoff long legend text.
    hideGraphOnMobile,
    alignCenterOnMobile,
    desc, // hide graph on mobile, but show legend
    legendLinksAsButton, // loads link at JS side instead of link for SEO perpose,
  } = props;

  const processPieChartData = (data = []) => {
    let result = [...data];
    const segmentColors = getPieChartSegmentColors(colorScheme, result.length);
    result = result.map((item, index) =>
      item.color
        ? item
        : {
            ...item,
            color: segmentColors[index],
          }
    );
    return result;
  };

  const graphDataProcesssed = processPieChartData(pieChartData || []);
  const legendDataProcesssed = processPieChartData(pieChartData || []);

  return (
    <div
      className={classnames(
        classes['pie-chart-wrapper'],
        'd-flex flex-column flex-md-row',
        !hideGraphOnMobile
          ? `align-items-md-start align-items-${alignCenterOnMobile ? 'center' : 'start'}`
          : 'align-items-center',
      )}
    >
      <div
        className={classnames(
          'pie-chart-graph-wrapper',
          { [classes.clickable]: chartIsClickable },
          { 'd-none d-md-block': hideGraphOnMobile },
          { 'align-self-center': alignCenterOnMobile },
        )}
      >
        <PieChartGraph
          desc={desc}
          diameter={pieChartDiameter}
          chartData={graphDataProcesssed}
          segmentsTooltipStyles={segmentsTooltipStyles}
          chartIsClickable={chartIsClickable}
          graphHeader={graphHeader}
          graphFooter={!isGraphFooterAfterLegend && graphFooter}
        />
      </div>
      {showLegends && (
        <div className={`pie-chart-legend-wrapper ${compact ? 'compact' : ''}`}>
          <PieChartLegend
            chartData={legendDataProcesssed}
            samePageLinks={samePageLinks}
            legendHeader={legendHeader}
            legendFooter={legendFooter}
            legendLinksAsButton={legendLinksAsButton}
          />
        </div>
      )}
      {isGraphFooterAfterLegend && graphFooter}
    </div>
  );
};

const propTypes = {
  pieChartDiameter: PropTypes.number,
  pieChartData: PropTypes.arrayOf(PropTypes.any),
  colorScheme: PropTypes.oneOf([
    PIE_CHART_COLOR_SCHEME.BLUE,
    PIE_CHART_COLOR_SCHEME.GREEN,
    PIE_CHART_COLOR_SCHEME.PURPLE,
  ]),
  segmentsTooltipStyles: PropTypes.objectOf(PropTypes.any),
  chartIsClickable: PropTypes.bool,
  samePageLinks: PropTypes.bool,
  legendHeader: PropTypes.any,
  legendFooter: PropTypes.any,
  graphHeader: PropTypes.any,
  graphFooter: PropTypes.any,
  isGraphFooterAfterLegend: PropTypes.bool,
  showLegends: PropTypes.bool,
  legendLinksAsButton: PropTypes.bool,
  compact: PropTypes.bool,
  hideGraphOnMobile: PropTypes.bool,
  alignCenterOnMobile: PropTypes.bool,
  desc: PropTypes.bool,
  blurLegends: PropTypes.bool,
  blurLegendCriteria: PropTypes.func
};

const defaultProps = {
  pieChartDiameter: 240,
  pieChartData: [],
  colorScheme: PIE_CHART_COLOR_SCHEME.GREEN,
  segmentsTooltipStyles: {},
  chartIsClickable: false,
  samePageLinks: false,
  legendHeader: '',
  legendFooter: '',
  graphHeader: '',
  graphFooter: '',
  isGraphFooterAfterLegend: false,
  showLegends: true,
  compact: false,
  hideGraphOnMobile: true,
  alignCenterOnMobile: false,
  desc: true,
  legendLinksAsButton: false,
  blurLegends: false,
  blurLegendCriteria: () => false
};

PieChart.propTypes = propTypes;
PieChart.defaultProps = defaultProps;

export default PieChart;
